/** @jsxImportSource @emotion/react */

import { upsertTournamentUser } from 'modules/players/store/actions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { Player, Team } from '../../domain/interfaces/player';
import playersRepository from '../../domain/repositories/player.repository';
import { resetMomentsData, selectCurrentPlayerId, selectPlayerById, setCurrentPlayerId } from '../../store';
import PlayerForm, { SubmitPlayerForm } from '../components/player-form';
import PlayerMomentsPage from '../components/player-moments-page';
import PlayerLoad from '../components/player-load';
import PlayerEditFormTabs from '../components/tabs-player-view';
import TeamsList from '../components/teams-list';
import useTournamentUsers from '../hooks/use-tournament-users';
import { selectTeamById } from 'modules/teams/store/teams';
import { useTeamById } from 'modules/teams/ui/hooks/use-team-by-id';

function PlayerViewPage() {
  const dispatch = useDispatch<AppDispatch>();

  const id = Number(useParams().id);
  const navigate = useNavigate();
  if (isNaN(id)) {
    navigate(Pages.PLAYERS.INDEX);
  }

  const location = useLocation();
  const activeTab = (location.pathname || '').split('/').at(-1);

  const { info } = useOrganizer();
  const [isLoading, setIsLoading] = useState(true);
  const [player, setPlayer] = useState<Player>();
  const findPlayer = useSelector(selectPlayerById(id));
  const { isProcessed } = useTournamentUsers();
  const currentPlayerId = useSelector(selectCurrentPlayerId);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const { setHeaderContent } = useMainLayout();

  const teamId = Number(useLocation().state?.fromTeam);
  const teamFromStore = useSelector(selectTeamById(teamId));
  const { data: fromTeam, isLoading: teamFetching } = useTeamById(teamId, {
    initialData: teamFromStore,
  });

  useEffect(() => {
    if (isProcessed) {
      playersRepository.getUser(id).then((player) => {
        setPlayer(player);
        setIsLoading(false);
      });
    }
  }, [id, isProcessed]);

  useEffect(() => {
    if (findPlayer === undefined) {
      playersRepository.getUser(id).then((player) => {
        setPlayer(player);
        setIsLoading(false);
      });
    } else {
      setPlayer(findPlayer);
      setIsLoading(false);
    }
  }, [findPlayer, id]);

  useEffect(() => {
    if (id && id !== currentPlayerId) {
      dispatch(resetMomentsData());
      dispatch(setCurrentPlayerId({ playerId: id }));
    }
  }, [currentPlayerId, dispatch, id]);

  const fullName = useMemo(() => {
    return player && `${player?.surname} ${player?.name} ${player?.middleName ?? ''}`;
  }, [player]);

  const breadcrumbs = useMemo(() => {
    if (fromTeam) {
      const items = [
        { label: 'Команды', to: Pages.TEAMS.INDEX },
        { label: fromTeam.name, to: Pages.TEAMS.builders.edit(fromTeam.id) },
        { label: 'Игроки', to: Pages.TEAMS.builders.edit(fromTeam.id, 'players') },
        {
          label: fullName ?? 'Неизвестный игрок',
          to: Pages.PLAYERS.builders.view(id),
          state: { fromTeam: fromTeam.id },
        },
      ];

      if (activeTab === 'teams') {
        items.push({
          label: 'Команды',
          to: Pages.PLAYERS.builders.view(id, 'teams'),
          state: { fromTeam: fromTeam.id },
        });
      }

      if (activeTab === 'moments') {
        items.push({
          label: 'Моменты',
          to: Pages.PLAYERS.builders.view(id, 'moments'),
          state: { fromTeam: fromTeam.id },
        });
      }

      return items;
    }

    const result = [
      { label: 'Игроки', to: Pages.PLAYERS.INDEX },
      { label: fullName ?? 'Неизвестный игрок', to: Pages.PLAYERS.builders.view(id) },
    ];

    if (activeTab === 'teams') {
      result.push({ label: 'Команды', to: Pages.PLAYERS.builders.view(id, 'teams') });
    }

    if (activeTab === 'moments') {
      result.push({ label: 'Моменты', to: Pages.PLAYERS.builders.view(id, 'moments') });
    }

    return result;
  }, [activeTab, fullName, id, fromTeam]);

  const breadcrumb = useMemo(() => <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbs} />, [breadcrumbs]);

  const teams = useMemo(() => {
    return (player?.positionsInTeams?.map((positionInTeam) => positionInTeam.team).filter(Boolean) as Team[]) ?? [];
  }, [player?.positionsInTeams]);

  const handleSubmit = useCallback<SubmitPlayerForm>(
    ({ values, formikHelpers }) => {
      if (!info) {
        return;
      }

      const photo = values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined;
      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      setIsLoadingPlayers(true);

      dispatch(
        upsertTournamentUser({
          userId: values?.id,
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          height,
          weight,
          photo,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success('Игрок успешно сохранён');
        })
        .finally(() => {
          formikHelpers.setSubmitting(false);
          setIsLoadingPlayers(false);
        });
    },
    [dispatch, info],
  );

  const body = useMemo(() => {
    if (!info || isLoading || teamFetching) {
      return <PlayerLoad />;
    }

    if (!player) {
      return (
        <div className="container pt-6">
          <div className="flex-column flex-center mt-24">
            <div className="text body-text bold mb-2">Такой игрок не найден</div>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'teams':
        return (
          <div className="mt-6">
            <TeamsList player={player} teams={teams ?? []} />
          </div>
        );
      case 'common':
        return <PlayerForm initialValues={player} onSubmit={handleSubmit} isLoading={isLoadingPlayers || teamFetching} />;
      case 'moments':
        return <PlayerMomentsPage player={player} />;
      default:
        return null;
    }
  }, [activeTab, handleSubmit, info, isLoading, isLoadingPlayers, player, teamFetching, teams]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'container pt-6'}>
        <div className={'mb-6'}>{player ? <PlayerEditFormTabs state={{ fromTeam: teamId }} /> : <></>}</div>
        <div>{body}</div>
      </div>
    </>
  );
}

export default PlayerViewPage;
